// MP411 — The public domain is a configuration value, not a code change.
//
// Before this file the code named THREE candidate domains in ~60 places
// (app.x-one.com, *.xonecyberaware.com, *.ferryticxone.com — plus the
// Resilience standalone host) while the dev environment actually served
// from Firebase's `*.web.app` and Cloud Run's `*.run.app` hosts. None
// of the literals resolved, and choosing the beta domain would have
// meant a code PR touching every one of them.
//
// Now every surface reads ONE resolver:
//
//   resolvePublicHosts(env) → { hosts, issues }
//
// fed by six variables (api: as-is via apps/api/src/config.ts; Vite
// apps: the same names with a `VITE_` prefix, mapped in each app's
// src/lib/publicHosts.ts):
//
//   PUBLIC_APP_URL          customer web app origin (apps/web)
//   PUBLIC_API_URL          public api origin (Cloud Run)
//   PUBLIC_MARKETING_URL    marketing site origin (apps/marketing-site)
//   PUBLIC_GATEWAY_URL      X-One AI Gateway console origin (apps/ai-gateway)
//   PUBLIC_RESILIENCE_HOST  bare hostname of the Resilience STANDALONE
//                           packaging (MP738+) — the suite is invisible there
//   BRAND_DOMAIN            bare domain for brand mailboxes (legal@, privacy@,
//                           billing@, status@, hello@) on legal + public pages
//
// The defaults below are the values the `dev` environment serves TODAY
// (verified against the GitHub `dev` environment variables on
// 2026-09-14), so nothing changes until an operator sets a variable.
// Switching to the beta domain — and later the prod domain — is the
// runbook "Switching the public domain" in docs/deploy.md: DNS →
// Firebase Hosting custom domain → environment variables → redeploy.
// No code PR.
//
// `scripts/check-public-hosts.mjs` (CI, api + web check jobs) fails on
// any candidate-domain literal outside this file's defaults table and
// apps/api/src/config.ts, so the literals cannot creep back.
//
// Pure module: no env access, no Node/Vite globals. Callers pass the
// env map in. That is what lets the api, five Vite apps, the OpenAPI
// generator and the tests share one implementation.
/** Environment variable name per key. Vite apps prefix these with
 *  `VITE_` (`VITE_PUBLIC_APP_URL`, …, `VITE_BRAND_DOMAIN`). */
export const PUBLIC_HOST_ENV_KEYS = {
    appUrl: 'PUBLIC_APP_URL',
    apiUrl: 'PUBLIC_API_URL',
    marketingUrl: 'PUBLIC_MARKETING_URL',
    gatewayUrl: 'PUBLIC_GATEWAY_URL',
    resilienceHost: 'PUBLIC_RESILIENCE_HOST',
    brandDomain: 'BRAND_DOMAIN',
};
/** What the `dev` environment serves today. The ONLY place a host
 *  literal may live besides apps/api/src/config.ts (which layers the
 *  pre-MP411 local-dev fallbacks on top). Change these only when the
 *  dev hosting itself moves — the beta / prod domain is set through the
 *  environment variables, never here. */
export const DEFAULT_PUBLIC_HOSTS = Object.freeze({
    appUrl: 'https://cyber-awareness-saas-dev.web.app',
    apiUrl: 'https://cyber-aware-api-1000190428534.europe-west2.run.app',
    marketingUrl: 'https://x-one-marketing-dev.web.app',
    // The Gateway console's Firebase Hosting target (ai-gateway-console.yml,
    // added 2026-09-25). Override with PUBLIC_GATEWAY_URL; the custom domain
    // gateway.ferrytic.com is one DNS record + one variable when wanted.
    gatewayUrl: 'https://x-one-ai-gateway-console-dev.web.app',
    // Program doc: docs/programs/x-one-resilience-app-scoping.md. No consumer
    // switches on it until MP738 wires the standalone chrome.
    resilienceHost: 'resilience.x-one.cyber',
    // The registrable domain every brand mailbox hangs off (legal@,
    // privacy@, billing@, status@, hello@). Chosen 2026-09-17: ferrytic.com
    // is the company domain and now also carries support/sales/security in
    // support.ts, so the platform presents ONE registrable domain — one SPF,
    // one DKIM, one DMARC policy, one sending reputation to warm.
    brandDomain: 'ferrytic.com',
    appHost: 'cyber-awareness-saas-dev.web.app',
});
/** Resolve the public hosts from an env map. Missing / empty values
 *  fall back to `defaults` (DEFAULT_PUBLIC_HOSTS unless the caller
 *  layers its own — apps/api/src/config.ts does, to keep the pre-MP411
 *  local-dev fallbacks byte-identical). Malformed values fall back too
 *  and are reported in `issues` rather than thrown: a bad host must not
 *  take the api down or blank a page. */
export function resolvePublicHosts(env, defaults = DEFAULT_PUBLIC_HOSTS) {
    const issues = [];
    const overridden = [];
    function pick(key, normalize, reason) {
        const envKey = PUBLIC_HOST_ENV_KEYS[key];
        const raw = env[envKey];
        const trimmed = typeof raw === 'string' ? raw.trim() : '';
        if (!trimmed)
            return defaults[key];
        const normalized = normalize(trimmed);
        if (normalized === null) {
            issues.push({ key: envKey, value: trimmed, reason });
            return defaults[key];
        }
        overridden.push(key);
        return normalized;
    }
    const appUrl = pick('appUrl', normalizePublicOrigin, 'expected an http(s) URL');
    const apiUrl = pick('apiUrl', normalizePublicOrigin, 'expected an http(s) URL');
    const marketingUrl = pick('marketingUrl', normalizePublicOrigin, 'expected an http(s) URL');
    const gatewayUrl = pick('gatewayUrl', normalizePublicOrigin, 'expected an http(s) URL');
    const resilienceHost = pick('resilienceHost', normalizeHostname, 'expected a bare hostname');
    const brandDomain = pick('brandDomain', normalizeHostname, 'expected a bare domain');
    return {
        hosts: {
            appUrl,
            apiUrl,
            marketingUrl,
            gatewayUrl,
            resilienceHost,
            brandDomain,
            appHost: hostnameOf(appUrl) ?? defaults.appHost,
        },
        issues,
        overridden,
    };
}
/** `https://host[/base]` with no trailing slash, or null when the value
 *  is not an http(s) URL. A path is kept (an api can live under a
 *  prefix); query and hash are not. */
export function normalizePublicOrigin(raw) {
    let u;
    try {
        u = new URL(raw.trim());
    }
    catch {
        return null;
    }
    if (u.protocol !== 'http:' && u.protocol !== 'https:')
        return null;
    if (!u.hostname)
        return null;
    const path = u.pathname.replace(/\/+$/, '');
    return `${u.protocol}//${u.host}${path}`;
}
const HOSTNAME_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/;
/** Lower-cased bare hostname. Tolerates a pasted `https://host/` (the
 *  scheme and path are stripped) but rejects anything that is not a
 *  hostname afterwards. */
export function normalizeHostname(raw) {
    let s = raw.trim().toLowerCase();
    s = s.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
    s = s.replace(/[/?#].*$/, '');
    s = s.replace(/:\d+$/, '');
    if (!s || s.length > 253 || !HOSTNAME_RE.test(s))
        return null;
    return s;
}
/** Hostname of an origin string, or null when unparsable. */
export function hostnameOf(origin) {
    try {
        return new URL(origin).hostname.toLowerCase() || null;
    }
    catch {
        return null;
    }
}
const SURFACE_KEY = {
    app: 'appUrl',
    api: 'apiUrl',
    marketing: 'marketingUrl',
    gateway: 'gatewayUrl',
};
/** Absolute link on a public surface. `path` may be `''`, `/x`, `x`,
 *  or carry a query/hash; exactly one slash joins origin and path. Every
 *  email and redirect the api emits goes through here so a link can
 *  never be built from a stale literal. */
export function buildPublicLink(hosts, path = '', surface = 'app') {
    const origin = hosts[SURFACE_KEY[surface]].replace(/\/+$/, '');
    const p = path.trim();
    if (!p || p === '/')
        return origin;
    if (p.startsWith('?') || p.startsWith('#'))
        return `${origin}${p}`;
    return `${origin}/${p.replace(/^\/+/, '')}`;
}
/** `local@<brandDomain>` — the legal / status / marketing mailboxes.
 *  Support, sales and security addresses are NOT derived from the
 *  brand domain; they are the MP402 / MP407 constants in support.ts. */
export function brandMailbox(hosts, localPart) {
    const local = localPart.trim();
    if (!local || local.includes('@')) {
        throw new Error(`brandMailbox: local part must be a bare mailbox name, got "${localPart}"`);
    }
    return `${local}@${hosts.brandDomain}`;
}
/** `mailto:` href for a brand mailbox with an optional subject. */
export function brandMailto(hosts, localPart, subject) {
    const to = brandMailbox(hosts, localPart);
    return subject && subject.trim()
        ? `mailto:${to}?subject=${encodeURIComponent(subject.trim())}`
        : `mailto:${to}`;
}
/** Map `import.meta.env`-style keys (`VITE_PUBLIC_APP_URL`, …) onto the
 *  plain names the resolver reads. Each Vite app calls this from its
 *  src/lib/publicHosts.ts; `legacy` lets an app keep honouring the
 *  variable it read before MP411 (`VITE_APP_URL`, `VITE_API_URL`,
 *  `VITE_SITE_URL`, `VITE_AI_GATEWAY_URL`) with lower precedence. */
export function viteEnvToPublicHostEnv(env, legacy = {}) {
    const out = {};
    for (const key of Object.keys(PUBLIC_HOST_ENV_KEYS)) {
        const envKey = PUBLIC_HOST_ENV_KEYS[key];
        const primary = env[`VITE_${envKey}`];
        const fallbackKey = legacy[key];
        const fallback = fallbackKey ? env[fallbackKey] : undefined;
        out[envKey] = primary && primary.trim() ? primary : fallback;
    }
    return out;
}
//# sourceMappingURL=hosts.js.map